import {
  createContext, useContext, useEffect, useState, useRef,
  ReactNode, useCallback,
} from 'react';
import { apiClient } from '../api/client';
import {
  getStoredToken,
  setStoredToken,
  clearStoredToken,
} from './token';

interface User {
  id: number;
  username: string;
  role: 'student' | 'teacher';
  is_teacher?: boolean;
  classroom?: string;
  display_name?: string;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [loading, setLoading] = useState(true);
  const justLoggedInRef = useRef(false);

  // Mevcut token ile kullanıcıyı getir
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    if (justLoggedInRef.current) {
      justLoggedInRef.current = false;
      setLoading(false);
      return;
    }
    apiClient
      .get('/auth/me/')
      .then((res) => setUser(res.data))
      .catch(() => {
        clearStoredToken();
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiClient.post('/auth/login/', {
      username,
      password,
    });
    const newToken = res.data.token;
    setStoredToken(newToken);
    justLoggedInRef.current = true;
    setToken(newToken);
    const me = await apiClient.get('/auth/me/');
    setUser(me.data);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout/');
    } catch {
      // Yine de local'ı temizle
    }
    clearStoredToken();
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be inside AuthProvider');
  }
  return ctx;
}